<template>
  <rest-page :fetch-endpoint="fetchEndpoint" :key="key">
    <page-header-new :title="pageTitle" :breadcrumbs="breadcrumbs">
      <template slot="actions">
        <el-button type="primary" plain @click="saveSite" :disabled="readonly">
          <i class="ico icon-floppy" />
          {{ $t('Sites_Form_Save') }}
        </el-button>
        <el-tooltip
          :disabled="!unsavedChanges"
          :content="$t('Sites_Form_UnsavedChanges_Message')"
        >
          <span>
            <el-button
              type="primary"
              plain
              @click="publish"
              :disabled="
                readonly || !model.id || !model.pages.length || unsavedChanges
              "
            >
              <i class="ico icon-upload-cloud" />
              {{ $t('Sites_Form_Button_Publish') }}
            </el-button>
          </span>
        </el-tooltip>
        <el-button
          type="primary"
          plain
          @click="unPublish"
          :disabled="readonly || !model.id || !model.pages.length"
        >
          <i class="ico icon-trash" />
          {{ $t('Sites_Form_Button_Unpublish') }}
        </el-button>
      </template>
    </page-header-new>
    <page-content>
      <rest-form
        :save-endpoint="saveEndpoint"
        :model="model"
        ref="form"
        :after-save="afterSave"
        :disabled="readonly"
      >
        <form-section class="form-section" :title="$t('Sites_Form_Details')">
          <div v-if="published" class="last-published-date">
            Last published date: {{ published }}
          </div>
          <el-row>
            <el-col :span="8">
              <form-item :label="$t('Sites_Title_Field_Name')" prop="title">
                <el-input v-model="model.name" :maxlength="100" />
              </form-item>
            </el-col>
          </el-row>
        </form-section>
        <form-section :title="$t('Sites_Form_Pages')">
          <template v-for="(page, index) in model.pages">
            <div class="page-wrapper" :key="index">
              <el-dropdown class="close-icon" trigger="click">
                <i class="el-icon-more"></i>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item @click.native="deletePage(index)">
                    {{ $t('Sites_Form_Page_Button_Delete') }}
                  </el-dropdown-item>
                  <el-dropdown-item
                    v-if="page.id && hasForm(page)"
                    @click.native="formSubmissionsExport(page.id)"
                  >
                    {{ $t('Sites_Form_Page_Button_ExportFormSubmissions') }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
              <page
                v-model="model.pages[index]"
                :domain="model.domain"
                :subdomain="model.subdomain"
                :pages="model.pages"
              />
            </div>
          </template>
          <el-button
            type="primary"
            icon="el-icon-circle-plus-outline"
            plain
            @click="addPage"
          >
            {{ $t('Sites_Form_AddPage') }}
          </el-button>
        </form-section>
      </rest-form>
    </page-content>
    <export-dialog v-if="showExportDialog" :task-id="taskId" />
  </rest-page>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import FormItem from '@/common/components/form/FormItem'
import RestPage from '@/common/components/rest/RestPage'
import { customConfirm, projectEndpoint, projectRoute } from '@/core/helpers'
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import RestForm from '@/common/components/rest/RestForm'
import PageContent from '@/common/components/page/PageContent'
import { siteStatusEnum } from '@/modules/sites/enum/SiteStatusEnum'
import { mapGetters } from 'vuex'
import _ from 'lodash'
import Page from '@/modules/sites/components/Page'
import RestApi from '@/core/services/RestApi'
import moment from 'moment'
import ExportDialog from '@/modules/sites/components/ExportDialog'
import { EventBus } from '@/core/EventBus'
import { ReturnTypes } from '@/modules/sites/enum/ReturnTypesEnum'
import { v4 as uuidv4 } from 'uuid'

export default {
  name: 'SiteFormPage',
  components: {
    ExportDialog,
    Page,
    PageContent,
    RestForm,
    PageHeaderNew,
    RestPage,
    FormItem,
    FormSection
  },

  props: {
    readonly: {
      default: false,
      type: Boolean
    },
    copy: {
      default: false,
      type: Boolean
    }
  },

  data() {
    return {
      key: null,
      published: null,
      showExportDialog: false,
      taskId: null,
      siteStatusEnum,
      initialStateModel: {},
      regex: /<form[^>]*>/,
      model: {
        name: null,
        status: 'draft',
        pages: []
      },
      defaultPage: {
        uuid: null,
        name: '',
        page_json: null,
        page_html: null,
        return_type: null,
        return_url: null,
        return_page_uuid: undefined,
        status: ''
      }
    }
  },

  created() {
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
  },

  mounted() {
    EventBus.$on('export-dialog-hide', () => (this.showExportDialog = false))
    this.initialStateModel = _.cloneDeep(this.model)
    if (!this.isMicrositesAvailable) {
      this.$router.push('/')
    }
  },

  destroyed() {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
  },

  beforeRouteLeave(to, from, next) {
    if (_.isEqual(this.model, this.initialStateModel) || to.query.type) {
      next()
    } else {
      customConfirm(
        this.$t('Site_Confirm_Title'),
        this.$t('Site_Confirm_Message'),
        this.$t('Site_Confirm_Button_Leave')
      )
        .then(() => {
          next()
        })
        .catch(() => {
          next(false)
        })
    }
  },

  computed: {
    ...mapGetters(['isMicrositesAvailable']),
    pageTitle() {
      return this.$t(this.$route.meta.title)
    },

    breadcrumbs() {
      return [
        {
          label: this.$t('Sites_Breadcrumb_Content')
        },
        {
          label: this.$t('Sites_Breadcrumb_Sites'),
          route: projectRoute({ name: 'Sites' })
        },
        {
          label: this.model.name || this.$t('Sites_Breadcrumb_Site')
        }
      ]
    },

    fetchEndpoint() {
      if (this.$route.query.id) {
        return projectEndpoint({
          method: 'get',
          url: `sites/${this.$route.query.id}`,
          handler: data => {
            this.model = _.cloneDeep(data)
            this.published = this.model.published
            // Clear return_page_uuid when return_type is empty
            this.model.pages.map(page => {
              if (page.return_type === null) {
                page.return_page_uuid = undefined
              }
              return page
            })
            if (this.copy) {
              this.model.id = null
              this.model.name += ' Copy'
              this.model.pages.forEach((page, index) => {
                this.model.pages[index].uuid = uuidv4().replace(/-/g, '')
                this.model.pages[index].hit_count = null
                this.model.pages[index].form_submission_count = null
                this.model.pages[index].return_page_uuid = null
                if (page.return_type !== ReturnTypes.CUSTOM_URL) {
                  this.model.pages[index].return_type = null
                  this.model.pages[index].return_page_uuid = undefined
                }
                this.model.pages[index].id = null
              })
              this.model.subdomain = null
              this.model.created = null
              this.model.updated = null
              this.model.uuid = null
              this.model.published = null
            }
            this.$nextTick(() => {
              this.initialStateModel = _.cloneDeep(this.model)
            })
          }
        })
      }

      return null
    },

    saveEndpoint() {
      if (this.model.id) {
        return projectEndpoint({
          method: 'patch',
          url: `sites/${this.model.id}`,
          data: this.model
        })
      }
      return projectEndpoint({
        method: 'post',
        url: 'sites',
        data: this.model
      })
    },
    publishEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `sites/publish`,
        params: { id: this.model.id }
      })
    },
    unPublishEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `sites/unpublish`,
        params: { id: this.model.id }
      })
    },
    unsavedChanges() {
      return !_.isEqual(this.model, this.initialStateModel)
    }
  },

  methods: {
    beforeUnloadHandler: function handler(event) {
      if (!_.isEqual(this.model, this.initialStateModel)) {
        event.preventDefault()
        event.returnValue = ''
      } else {
        delete event['returnValue']
      }
    },

    saveSite() {
      this.$refs.form.save()
    },

    publish() {
      customConfirm(
        this.$t('Sites_Form_Publish_Confirm_Title'),
        this.$t('Sites_Form_Publish_Confirm_Description'),
        this.$t('Sites_Form_Button_Publish')
      )
        .then(() => {
          RestApi(this.publishEndpoint)
            .then(() => {
              this.published = moment().format('YYYY-MM-DD HH:mm:ss')
              this.$message({
                message: this.$t('Sites_Form_Publish_SuccessMessage'),
                type: 'success'
              })
            })
            .catch(error => {
              this.$message({ message: error.message, type: 'error' })
            })
        })
        .catch(_ => {})
    },
    unPublish() {
      customConfirm(
        this.$t('Sites_Form_Unpublish_Confirm_Title'),
        this.$t('Sites_Form_Unpublish_Confirm_Description'),
        this.$t('Sites_Form_Button_Unpublish')
      )
        .then(() => {
          RestApi(this.unPublishEndpoint)
            .then(() => {
              this.published = null
              this.$message({
                message: this.$t('Sites_Form_Unpublish_SuccessMessage'),
                type: 'success'
              })
            })
            .catch(error => {
              this.$message({ message: error.message, type: 'error' })
            })
        })
        .catch(_ => {})
    },

    deletePage(index) {
      this.$msgbox({
        title: this.$t('Common_Popup_Delete_Title'),
        message: this.$t('Common_Popup_Delete_Message'),
        showCancelButton: true,
        confirmButtonText: this.$t('Common_Popup_Delete_Confirm'),
        cancelButtonText: this.$t('Common_Popup_Delete_Cancel'),
        center: true
      }).then(() => {
        this.model.pages.splice(index, 1)
      })
    },

    afterSave(model, finishLoading) {
      this.initialStateModel = _.cloneDeep(this.model)
      if (this.copy || !this.$route.query.id) {
        this.$router.push(
          projectRoute({ name: 'SiteUpdate', query: { id: model.id } })
        )
      }
      this.key = new Date().getTime()
      finishLoading.apply()
    },

    addPage() {
      let page = { ...this.defaultPage }
      page.uuid = uuidv4().replace(/-/g, '')
      this.model.pages.push(page)
    },

    hasForm(page) {
      return page.page_html && this.regex.test(page.page_html)
    },

    formSubmissionsExport(id) {
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `sites/form-submission-export`,
          params: { id }
        })
      )
        .then(({ data }) => {
          this.taskId = data.task_id
          this.showExportDialog = true
        })
        .catch(error => {
          this.$message({ message: error.message, type: 'error' })
        })
    }
  }
}
</script>
<style scoped lang="scss">
.form-section {
  position: relative;
  .last-published-date {
    position: absolute;
    top: 20px;
    right: 20px;
    color: #909399;
  }
}
.page-wrapper {
  position: relative;
  .close-icon {
    cursor: pointer;
    position: absolute;
    right: 7px;
    top: 7px;
    z-index: 99;
  }
  .el-icon-more {
    transform: rotate(90deg);
    font-size: 14px;
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/sites/pages/SiteFormPage.vue